import React, { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import ReplayIcon from '@mui/icons-material/Replay';
import moment from 'moment';
import {
  Link,
  useHistory,
  useParams,
} from 'react-router-dom';

import {
  AccountTable,
  DashboardError,
  DashboardPage,
  IAccountTableColumn,
  ListingDetails,
} from '../../components/JobsAccount';
import { NoAccessMessage } from '../../components/NoAccessMessage';
import { book } from '../../routers/books';
import { useGetJobsPrepaidDetailQuery } from '../../services/serviceJobsAccount';
import { IJobListing } from '../../services/serviceJobsAccount/interfaces';
import {
  getEditJobPath,
  getPostPrepaidJobPath,
  getPrepaidPurchasePath,
  getReactivateJobPath,
} from '../../utils/jobAccountRoutes';
import { isForbidden } from '../../utils/jobAccessError';

const formatDate = (date: string | null | undefined): string => (
  date ? moment(date).format('MM/DD/YYYY') : '—'
);

export const JobsPrepaidDetail = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [tab, setTab] = useState<'active' | 'inactive'>('active');
  const query = useGetJobsPrepaidDetailQuery(id, { skip: !id });
  const { data, error, isLoading, isFetching } = query;

  const columns = (isActive: boolean): IAccountTableColumn<IJobListing>[] => [
    {
      id: 'details',
      label: 'Details',
      render: (listing) => <ListingDetails listing={listing} isActive={isActive} />,
    },
    {
      id: 'dated',
      label: isActive ? 'Expires' : 'Created',
      render: (listing) => formatDate(listing.dated),
    },
    {
      id: 'applicants',
      label: 'Applicants',
      align: 'center',
      render: (listing) => (
        <Button
          size="small"
          onClick={() => history.push(book.jobsApplicants(listing.eguid))}
        >
          {listing.applicantCount}
        </Button>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (listing) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="View applicants">
            <IconButton
              size="small"
              onClick={() => history.push(book.jobsApplicants(listing.eguid))}
              aria-label="View applicants"
            >
              <PeopleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isActive ? (
            <Tooltip title="Edit listing">
              <IconButton
                size="small"
                onClick={() => history.push(getEditJobPath(
                  listing.siteId ?? data?.siteId,
                  listing.eguid
                ))}
                aria-label="Edit listing"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Reactivate listing">
              <IconButton
                size="small"
                onClick={() => history.push(getReactivateJobPath(
                  listing.siteId ?? data?.siteId,
                  listing.eguid
                ))}
                aria-label="Reactivate listing"
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const stats = [
    { label: 'Listing Type', value: data ? (data.isFeatured ? 'Featured' : 'Standard') : '—' },
    { label: 'Listing Credits Purchased', value: data?.totalCount ?? '—' },
    { label: 'Listing Credits Remaining', value: data?.remainingCount ?? '—' },
    { label: 'Purchase Date', value: formatDate(data?.purchaseDate) },
    { label: 'Expiration Date', value: formatDate(data?.expirationDate) },
  ];
  const rows = tab === 'active' ? data?.activeJobs || [] : data?.inactiveJobs || [];

  return (
    <DashboardPage
      title={data
        ? `Prepaid Job Package #${data.prepaidId}: ${data.company}`
        : 'Prepaid Job Package'}
      isLoading={isLoading}
      noAccessComponent={isForbidden(error) ? <NoAccessMessage /> : undefined}
      actions={data ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {data.isCurrent ? (
            <Button
              variant="contained"
              onClick={() => history.push(getPostPrepaidJobPath(
                data.siteId,
                data.prepaidGuid
              ))}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Use Listing Credit
            </Button>
          ) : (
            <Button
              component={Link}
              to={book.jobsPrepaid}
              variant="outlined"
            >
              Back to Packages
            </Button>
          )}
          <Button
            component={Link}
            to={getPrepaidPurchasePath(data.siteId)}
            variant={data.isCurrent ? 'outlined' : 'contained'}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Buy Another Package
          </Button>
        </Stack>
      ) : undefined}
    >
      <DashboardError error={error} fallback="We could not load this prepaid package." />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md key={stat.label}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5 }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tab}
        onChange={(_event, value: 'active' | 'inactive') => setTab(value)}
        sx={{ mb: 2 }}
        aria-label="Package listing status"
      >
        <Tab label={`Active (${data?.activeJobs?.length || 0})`} value="active" />
        <Tab label={`Inactive (${data?.inactiveJobs?.length || 0})`} value="inactive" />
      </Tabs>

      <AccountTable
        ariaLabel={`${tab} prepaid package listings`}
        columns={columns(tab === 'active')}
        rows={rows}
        getRowId={(listing) => listing.id}
        emptyMessage={`There are no ${tab} listings in this package.`}
        isLoading={isFetching}
      />
    </DashboardPage>
  );
};

export default JobsPrepaidDetail;
